/**
 * Keyboard → DirectorAction mapping (see docs/GAME_DESIGN.md "Controls").
 * Keys are matched by `event.code`, so Shift+digit works on every layout.
 */
import type { DirectorAction } from './actions';

export interface KeyboardOptions {
  /** Camera ids in hotkey order: camIds[0] = key 1. */
  camIds: readonly string[] | (() => readonly string[]);
  /** While true, digits switch replay angle instead of hot-punching. */
  isReplayActive?: () => boolean;
  /** Return false to suspend input (menus, pause). */
  enabled?: () => boolean;
  target?: Window | HTMLElement;
}

function isTypingTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  if (t.isContentEditable) return true;
  const tag = t.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') {
    const type = (t as HTMLInputElement).type;
    return !['button', 'checkbox', 'radio', 'range', 'submit', 'reset', 'color'].includes(type);
  }
  return false;
}

function digitOf(code: string): number | null {
  const m = /^(?:Digit|Numpad)([1-9])$/.exec(code);
  return m ? Number(m[1]) : null;
}

/** Pure mapping, exported for tests/bots. Returns null if the key is not bound. */
export function keyToAction(
  code: string, shift: boolean, camIds: readonly string[], replayActive: boolean,
): DirectorAction | null {
  const d = digitOf(code);
  if (d !== null) {
    const camId = camIds[d - 1];
    if (!camId) return null;
    if (shift) return { type: 'preview', camId };
    return replayActive ? { type: 'replay_angle', camId } : { type: 'hot_punch', camId };
  }
  switch (code) {
    case 'Space': return { type: 'take' };
    case 'KeyM': return { type: 'mix' };
    case 'KeyR': return { type: 'replay_toggle' };
    case 'Escape': return { type: 'replay_end' };
    case 'KeyQ': return { type: 'graphic_context' };
    case 'KeyW': return { type: 'graphic_toggle', kind: 'match_stats' };
    case 'KeyE': return { type: 'graphic_toggle', kind: 'possession_bar' };
    case 'KeyS': return { type: 'scorebug_toggle' };
    case 'KeyA': return { type: 'graphics_clear' };
    case 'Tab': return { type: 'booth_focus', dir: shift ? -1 : 1 };
    default: return null;
  }
}

/** Bind director hotkeys. Returns a disposer. */
export function bindDirectorKeys(onAction: (a: DirectorAction) => void, opts: KeyboardOptions): () => void {
  const target = opts.target ?? window;
  const handler = (ev: Event): void => {
    const e = ev as KeyboardEvent;
    if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
    if (isTypingTarget(e.target)) return;
    if (opts.enabled && !opts.enabled()) return;
    const ids = typeof opts.camIds === 'function' ? opts.camIds() : opts.camIds;
    const action = keyToAction(e.code, e.shiftKey, ids, opts.isReplayActive?.() ?? false);
    if (!action) return;
    e.preventDefault(); // Space scroll, Tab focus, etc.
    if (e.repeat && action.type !== 'booth_focus') return;
    onAction(action);
  };
  target.addEventListener('keydown', handler);
  return () => target.removeEventListener('keydown', handler);
}
